/**
 * Traitement du profil d'altitude : estimation du bruit, lissage adaptatif
 * et calcul du dénivelé avec hystérésis.
 *
 * Portage de `GpxTrack.kt:694-816`. Ces trois étapes forment une chaîne :
 * modifier l'une d'elles change les D+/D− affichés. Les constantes sont
 * verrouillées par les tests de référence de l'application Android.
 */

/** Distance de lissage minimale, en mètres. */
export const SMOOTHING_BASE_DISTANCE = 80.0

/** Poids du bruit dans l'élargissement de la fenêtre de lissage. */
export const SMOOTHING_NOISE_COEFF = 750.0

/** Plafond de la distance de lissage, en mètres. */
export const SMOOTHING_MAX_DISTANCE = 500.0

/**
 * Amplitude minimale d'un mouvement vertical pour être comptabilisé, en mètres.
 *
 * En dessous, l'oscillation est considérée comme du bruit du capteur : sans ce
 * seuil, une trace plate accumulerait des centaines de mètres de dénivelé.
 */
export const ELEVATION_THRESHOLD = 3.0

/**
 * Écart-type de la dérivée seconde discrète des altitudes.
 *
 * Sert d'estimateur du bruit du capteur : une trace bruitée a une dérivée
 * seconde erratique, une trace propre une dérivée seconde proche de zéro.
 */
export function elevationNoiseSigma(elevations: readonly number[]): number {
  if (elevations.length < 3) {
    return 0
  }

  const n = elevations.length - 2
  let sum = 0
  let sumSq = 0

  for (let i = 1; i <= elevations.length - 2; i++) {
    const d2 = elevations[i + 1]! - 2 * elevations[i]! + elevations[i - 1]!
    sum += d2
    sumSq += d2 * d2
  }

  const mean = sum / n
  const variance = sumSq / n - mean * mean

  return variance > 0 ? Math.sqrt(variance) : 0
}

/**
 * Demi-largeur de la fenêtre de lissage, en nombre de points.
 *
 * La fenêtre s'élargit avec le bruit mesuré et se resserre quand les points
 * sont espacés — l'objectif est une distance de lissage à peu près constante
 * au sol, quel que soit l'échantillonnage du GPX.
 */
export function smoothingHalfWindow(
  elevations: readonly number[],
  distances: readonly number[],
): number {
  if (distances.length < 2) {
    return 0
  }

  const span = distances[distances.length - 1]! - distances[0]!
  const avgSpacing = span / (distances.length - 1)

  if (avgSpacing <= 0) {
    return 0
  }

  const sigma = elevationNoiseSigma(elevations)
  const targetDistance = Math.min(
    SMOOTHING_BASE_DISTANCE + (SMOOTHING_NOISE_COEFF * sigma * sigma) / avgSpacing,
    SMOOTHING_MAX_DISTANCE,
  )

  return Math.round(targetDistance / (2 * avgSpacing))
}

/**
 * Moyenne glissante centrée, calculée par sommes préfixes.
 *
 * La fenêtre est tronquée aux bords plutôt que d'être repliée : les premiers
 * et derniers points sont moyennés sur moins de voisins, ce qui évite
 * d'inventer du relief aux extrémités de la trace.
 */
export function smoothElevations(
  elevations: readonly number[],
  half: number,
): number[] {
  if (half <= 0 || elevations.length === 0) {
    return [...elevations]
  }

  const n = elevations.length
  const prefix = new Float64Array(n + 1)
  for (let i = 0; i < n; i++) {
    prefix[i + 1] = prefix[i]! + elevations[i]!
  }

  const result = new Array<number>(n)
  for (let i = 0; i < n; i++) {
    const start = Math.max(i - half, 0)
    const end = Math.min(i + half + 1, n)
    result[i] = (prefix[end]! - prefix[start]!) / (end - start)
  }

  return result
}

export interface ProfileSamples {
  readonly distances: readonly number[]
  readonly elevations: readonly number[]
}

/**
 * Réduit un profil à ce qu'un écran peut réellement montrer.
 *
 * Tracer deux cent mille segments dans une case de quelques centaines de
 * pixels coûte cher et ne se voit pas : plusieurs centaines de points tombent
 * dans la même colonne. On ne garde par colonne que ce qui décide de la
 * silhouette — le premier point, les extrêmes, le dernier — dans leur ordre
 * d'origine, ce qui préserve pics et creux au pixel près.
 *
 * Portage de la réduction par colonne de `ElevationProfileView` (Android).
 *
 * @param columns Nombre de colonnes de pixels disponibles.
 */
export function downsampleProfile(
  distances: readonly number[],
  elevations: readonly number[],
  columns: number,
): ProfileSamples {
  const count = Math.min(distances.length, elevations.length)

  // En dessous de deux points par colonne, il n'y a rien à gagner : la
  // réduction coûterait un parcours de plus pour le même tracé.
  if (columns < 1 || count <= columns * 2) {
    return { distances, elevations }
  }

  const first = distances[0]!
  const span = distances[count - 1]! - first
  if (span <= 0) return { distances, elevations }

  const keep: number[] = []
  let column = -1
  let colFirst = 0
  let colLast = 0
  let colMin = 0
  let colMax = 0

  const flush = (): void => {
    if (column < 0) return
    // Trié par rang : la courbe se lit dans l'ordre du parcours, et deux
    // indices confondus ne sont retenus qu'une fois.
    for (const index of [colFirst, colMin, colMax, colLast].sort((a, b) => a - b)) {
      if (keep[keep.length - 1] !== index) keep.push(index)
    }
  }

  for (let i = 0; i < count; i++) {
    const c = Math.min(
      Math.floor(((distances[i]! - first) / span) * columns),
      columns - 1,
    )

    if (c !== column) {
      flush()
      column = c
      colFirst = i
      colMin = i
      colMax = i
    } else {
      if (elevations[i]! < elevations[colMin]!) colMin = i
      if (elevations[i]! > elevations[colMax]!) colMax = i
    }

    colLast = i
  }

  flush()

  return {
    distances: keep.map((index) => distances[index]!),
    elevations: keep.map((index) => elevations[index]!),
  }
}

export interface ElevationGainLoss {
  readonly gain: number
  readonly loss: number
}

/**
 * Dénivelé positif et négatif, avec hystérésis.
 *
 * Machine à trois états. On suit un extremum courant sans rien compter, et
 * l'on ne valide une montée (ou une descente) qu'une fois qu'elle s'est
 * inversée d'au moins `threshold` mètres — ce qui filtre les allers-retours
 * du capteur tout en conservant le relief réel.
 */
export function calculateElevationWithHysteresis(
  elevations: readonly number[],
  threshold = ELEVATION_THRESHOLD,
  fromIndex = 0,
): ElevationGainLoss {
  if (fromIndex >= elevations.length) {
    return { gain: 0, loss: 0 }
  }

  let gain = 0
  let loss = 0

  const first = elevations[fromIndex]!
  let currentMax = first
  let currentMin = first
  let lastRecorded = first

  /** `null` tant que le sens de la pente n'est pas encore établi. */
  let goingUp: boolean | null = null

  for (let i = fromIndex + 1; i < elevations.length; i++) {
    const ele = elevations[i]!

    if (goingUp === null) {
      // Phase d'amorçage : on élargit l'intervalle jusqu'à franchir le seuil.
      if (ele > currentMax) currentMax = ele
      if (ele < currentMin) currentMin = ele

      if (currentMax - currentMin >= threshold) {
        if (ele >= currentMax - threshold / 2) {
          goingUp = true
          lastRecorded = currentMin
          currentMax = ele
        } else {
          goingUp = false
          lastRecorded = currentMax
          currentMin = ele
        }
      }
    } else if (goingUp) {
      if (ele > currentMax) {
        currentMax = ele
      } else if (currentMax - ele >= threshold) {
        // Inversion confirmée : la montée qui précède est validée.
        gain += currentMax - lastRecorded
        lastRecorded = currentMax
        goingUp = false
        currentMin = ele
      }
    } else {
      if (ele < currentMin) {
        currentMin = ele
      } else if (ele - currentMin >= threshold) {
        loss += lastRecorded - currentMin
        lastRecorded = currentMin
        goingUp = true
        currentMax = ele
      }
    }
  }

  // Le dernier mouvement n'a pas été inversé : on le compte s'il est positif.
  if (goingUp === true) {
    const diff = currentMax - lastRecorded
    if (diff > 0) gain += diff
  } else if (goingUp === false) {
    const diff = lastRecorded - currentMin
    if (diff > 0) loss += diff
  }

  return { gain, loss }
}
